// The achievement badges (achievements.md §8, ui-map-interface.md §6.1).
//
// One badge is two things drawn one over the other: the GRADE RING, which
// says how deep the player has been, and the SILHOUETTE, which says what
// the achievement counts. Twenty-one silhouettes, one per category of
// `ACHIEVEMENT_CATEGORIES`; six grades, one ink each from
// `ACHIEVEMENT_LOOK.gradeInk`.
//
// **The geometry lives in ONE place, on purpose.** The owner picked card
// 02 "depth gauge" over card 07 "the marks set" of mockup round forty-four
// (R962). The pick swapped `drawRing` alone: the provisional ring of R937
// was card 01, and the descriptions, the scale, the cache and
// every caller stayed as they were.
//
// **The rule of a description** (marks.ts): a silhouette lays down paths
// and never calls `fill()` or `stroke()`. `drawBadge` is the master and
// picks the ink, which is how one description gives the filled reading
// and the hollow one. `drawBadge` itself paints, the way the site marks
// of `marks.ts` do: a badge carries several colours and no caller chooses
// them.
//
// **The design box is 48 units across** and the silhouette box is ±13, so
// `sizePx / BADGE_BOX` is the whole scale and no shape changes with size.
// `MarkGraphics` carries no transform, so `ScaledMarks` below multiplies
// every coordinate and every stroke width on the way through.
//
// The DOM reads a badge through `badgeCanvas`, which bakes once per
// (category, grade, px) and copies the bitmap afterwards — §10.2, split
// static from dynamic. Nothing here runs per frame.

import {
  ACHIEVEMENT_CATEGORIES, ACHIEVEMENT_LOOK, MARKER_NEUTRAL, PALETTE_GOLD,
} from '../gen/presentation';
import {
  Canvas2DMarks, band, bar, hexPoints, ring, starPoints,
  type MarkCap, type MarkFill, type MarkGraphics, type MarkJoin, type MarkStroke,
} from './marks';
import { mixColor, packHex } from './color';
import { deviceRatio, MAX_DPR } from './dpr';
import { TAU } from './math';
import { DEEP_INK } from './recipe-kit';

const PI = Math.PI;

/** The design box, in design units. `sizePx / BADGE_BOX` is the scale. */
export const BADGE_BOX = 48;

/** Under this size the hairline detail of a silhouette is dropped: at 24 px
 *  a 0.55 px line is a smear and not a line. */
const SMALL_PX = 40;

/** The plate a badge stands on — the chrome's deepest ground. */
const DEEP = DEEP_INK;
/** The white a Challenger badge is lifted with. */
const WHITE = MARKER_NEUTRAL;
/** A recognition wears the frozen gold: it holds no grade and no points,
 *  so it must not read as a grade (achievements.md §6). */
const GOLD = packHex(PALETTE_GOLD);

/** The six grade inks, as colours. Grade 1 is index 0. */
export const GRADE_INK: readonly number[] = ACHIEVEMENT_LOOK.gradeInk.map((s) => packHex(s));

/** The deepest grade, which is the length of the ink ladder. */
export const TOP_GRADE = GRADE_INK.length;

/** Every category id, in the catalog's reading order. */
export const BADGE_CATEGORIES: readonly string[] = ACHIEVEMENT_CATEGORIES.map((c) => c.id);

/** The silhouette id one category wears. */
const SILHOUETTE_OF = new Map(ACHIEVEMENT_CATEGORIES.map((c) => [c.id, c.silhouette]));

/**
 * One silhouette, as paths only.
 *
 * `body` lays the closed shapes, `line` the hairlines a small badge drops,
 * and `over` the one shape that is cut back OUT of the mass (a cap's star,
 * a rosette's centre).
 */
export interface BadgeSilhouette {
  body(g: MarkGraphics): void;
  line?(g: MarkGraphics): void;
  over?(g: MarkGraphics): void;
}

// ---------------------------------------------------------------------------
// The scale adapter
// ---------------------------------------------------------------------------

/**
 * A `MarkGraphics` that scales design units into pixels on the way through.
 *
 * The interface carries no transform, and a badge must draw at 64 px, at
 * 40 px and at 24 px from one description. Every coordinate, every radius
 * and every stroke width is multiplied by `k`; a fill and a stroke are
 * forwarded whole otherwise, so the path rule of `marks.ts` is unchanged.
 */
export class ScaledMarks implements MarkGraphics {
  constructor(
    private readonly g: MarkGraphics,
    private readonly k: number,
    private readonly cx = 0,
    private readonly cy = 0,
  ) {}

  private x(v: number): number { return this.cx + v * this.k; }
  private y(v: number): number { return this.cy + v * this.k; }

  moveTo(x: number, y: number): this { this.g.moveTo(this.x(x), this.y(y)); return this; }
  lineTo(x: number, y: number): this { this.g.lineTo(this.x(x), this.y(y)); return this; }

  arc(x: number, y: number, r: number, a0: number, a1: number, ccw = false): this {
    this.g.arc(this.x(x), this.y(y), r * this.k, a0, a1, ccw);
    return this;
  }

  closePath(): this { this.g.closePath(); return this; }

  poly(points: number[], close = true): this {
    const out = new Array<number>(points.length);
    for (let i = 0; i < points.length; i += 2) {
      out[i] = this.x(points[i]!);
      out[i + 1] = this.y(points[i + 1]!);
    }
    this.g.poly(out, close);
    return this;
  }

  circle(x: number, y: number, r: number): this {
    this.g.circle(this.x(x), this.y(y), r * this.k);
    return this;
  }

  fill(style: MarkFill): this { this.g.fill(style); return this; }

  stroke(style: MarkStroke): this {
    this.g.stroke({ ...style, width: style.width * this.k });
    return this;
  }
}

// ---------------------------------------------------------------------------
// The small path helpers the descriptions share
// ---------------------------------------------------------------------------

/** Rotate, scale and move a flat point array. A positive scale keeps the
 *  winding, which the nonzero fill rule depends on. */
function xf(p: readonly number[], a: number, dx: number, dy: number, s = 1): number[] {
  const ca = Math.cos(a), sa = Math.sin(a);
  const o: number[] = [];
  for (let i = 0; i < p.length; i += 2) {
    const x = p[i]! * s, y = p[i + 1]! * s;
    o.push(x * ca - y * sa + dx, x * sa + y * ca + dy);
  }
  return o;
}

/** A dashed leg, as segments: the API carries no dash. */
function dash(g: MarkGraphics, x0: number, y0: number, x1: number, y1: number, pitch: number) {
  const dx = x1 - x0, dy = y1 - y0;
  const len = Math.hypot(dx, dy);
  const n = Math.max(1, Math.round(len / pitch));
  for (let k = 0; k < n; k += 2) {
    g.moveTo(x0 + dx * (k / n), y0 + dy * (k / n));
    g.lineTo(x0 + dx * Math.min(1, (k + 1) / n), y0 + dy * Math.min(1, (k + 1) / n));
  }
}

/** One anchor of the fellowship badge: the shank, the flukes and the stock. */
function anchorBody(g: MarkGraphics, rot: number, dx: number) {
  g.poly(xf([-1.6, -11.6, 1.6, -11.6, 1.6, 7.8, -1.6, 7.8], rot, dx, 0));
  g.poly(xf([-7.4, 2.4, -5, 5.6, -8.8, 6.8], rot, dx, 0));
  g.poly(xf([7.4, 2.4, 5, 5.6, 8.8, 6.8], rot, dx, 0));
  g.poly(xf([-6.6, -7.2, 6.6, -7.2, 6.6, -5, -6.6, -5], rot, dx, 0));
}

/** The arm and the ring of one anchor. */
function anchorLine(g: MarkGraphics, rot: number, dx: number) {
  const p0 = xf([-7.8, 3], rot, dx, 0);
  const c = xf([0, -0.6], rot, dx, 0);
  const r = xf([0, -11.6], rot, dx, 0);
  g.moveTo(p0[0]!, p0[1]!);
  g.arc(c[0]!, c[1]!, 8.4, PI - 0.45 + rot, 0.45 + rot, false);
  g.moveTo(r[0]! + 2.6, r[1]!);
  g.arc(r[0]!, r[1]!, 2.6, 0, TAU, false);
}

// ---------------------------------------------------------------------------
// The twenty-one silhouettes (mockup round forty-four, card 07; the
// pennants of R1511 follow the same set)
//
// Every description lays paths in a ±13 box and never paints.
// ---------------------------------------------------------------------------

export const BADGE_SILHOUETTES: Readonly<Record<string, BadgeSilhouette>> = {
  /** industry — a dome. */
  dome: {
    body(g) {
      g.moveTo(-10.5, 3); g.arc(0, 3, 10.5, PI, 0, false);
      g.lineTo(10.5, 4.5); g.lineTo(-10.5, 4.5); g.closePath();
      g.poly([-12.5, 4.5, 12.5, 4.5, 12.5, 8.2, -12.5, 8.2]);
    },
    line(g) {
      g.moveTo(-5.4, -5.2); g.lineTo(-5.4, 4.5);
      g.moveTo(5.4, -5.2); g.lineTo(5.4, 4.5);
      g.moveTo(-5.6, 3); g.arc(0, 3, 5.6, PI, 0, false);
      g.moveTo(0, -7.5); g.lineTo(0, -11.5);
      g.moveTo(-2.4, -11.5); g.lineTo(2.4, -11.5);
    },
  },

  /** shipyard — a keel on the stocks. */
  keel: {
    body(g) {
      g.poly([-11.5, -8, -8, -8, -8, 3.5, 11.5, 3.5, 11.5, 6.8, -11.5, 6.8]);
    },
    line(g) {
      const ribs = [-4, 0.5, 5, 9];
      for (let i = 0; i < ribs.length; i++) {
        g.moveTo(ribs[i]!, 3.5);
        g.lineTo(ribs[i]! + 1.6 + i * 0.5, -5 + i * 0.9);
      }
      g.moveTo(-11.5, 9.8); g.lineTo(11.5, 9.8);
      g.moveTo(-6, 9.8); g.lineTo(-3.5, 6.8);
      g.moveTo(8, 9.8); g.lineTo(5.5, 6.8);
    },
  },

  /** arsenal — a torpedo. */
  torpedo: {
    body(g) {
      g.poly([-7.5, -4.2, 5.5, -4.2, 9.5, -2.6, 11.5, 0, 9.5, 2.6, 5.5, 4.2, -7.5, 4.2]);
      g.poly([-12, -7.6, -7.5, -4.2, -7.5, 4.2, -12, 7.6]);
    },
    line(g) {
      g.moveTo(-5.5, 0); g.lineTo(7, 0);
      g.moveTo(-2.5, -4.2); g.lineTo(-2.5, 4.2);
      g.moveTo(1.6, -4); g.lineTo(1.6, 4);
    },
  },

  /** research — a lamp. */
  lamp: {
    body(g) {
      g.poly([-6.5, -6.5, 6.5, -6.5, 9, 0.5, -9, 0.5]);
      g.poly([-9, 0.5, 9, 0.5, 6.8, 4.4, -6.8, 4.4]);
    },
    line(g) {
      g.moveTo(-9, -6.5); g.arc(0, -6.5, 9, PI, 0, false);
      g.moveTo(-5.4, 6.2); g.lineTo(-7.6, 10.6);
      g.moveTo(0, 6.4); g.lineTo(0, 11.4);
      g.moveTo(5.4, 6.2); g.lineTo(7.6, 10.6);
    },
  },

  /** invention — a compass rose of gears. */
  gears: {
    body(g) {
      ring(g, 0, 0, 12, 9.6);
      for (let k = 0; k < 8; k++) {
        const a = k * PI / 4;
        bar(g, Math.cos(a) * 11, Math.sin(a) * 11, Math.cos(a) * 13.4, Math.sin(a) * 13.4, 3.2);
      }
      g.poly(starPoints(4, 8.2, 2.1, -PI / 2));
    },
    line(g) {
      for (let k = 0; k < 4; k++) {
        const a = PI / 4 + k * PI / 2;
        g.moveTo(0, 0);
        g.lineTo(Math.cos(a) * 6, Math.sin(a) * 6);
      }
    },
  },

  /** prospecting — a dredge scoop. */
  scoop: {
    body(g) {
      g.poly([-9.5, -1.5, 9.5, -1.5, 7.4, 7.6, -7.4, 7.6]);
      for (const x of [-4.6, 0, 4.6]) g.poly([x - 2, 7.6, x + 2, 7.6, x, 11.4]);
    },
    line(g) {
      g.moveTo(-9.5, -1.5); g.lineTo(0, -8.6); g.lineTo(9.5, -1.5);
      g.moveTo(0, -8.6); g.lineTo(0, -12.4);
    },
  },

  /** treasury — a coin stack. */
  coins: {
    body(g) {
      for (const y of [3.4, -0.6, -4.6]) {
        g.poly([-7.4, y, 7.4, y, 8.4, y + 1.6, 7.4, y + 3.2, -7.4, y + 3.2, -8.4, y + 1.6]);
      }
      g.circle(0, -8.6, 4.6);
    },
    line(g) {
      g.moveTo(-2.2, -8.6); g.arc(0, -8.6, 2.2, PI, -PI, true);
      for (let k = 0; k < 4; k++) {
        const a = PI / 4 + k * PI / 2;
        g.moveTo(Math.cos(a) * 2.2, -8.6 + Math.sin(a) * 2.2);
        g.lineTo(Math.cos(a) * 4.4, -8.6 + Math.sin(a) * 4.4);
      }
    },
  },

  /** warfare — crossed darts, the combat mark of §6.1. */
  darts: {
    body(g) {
      const dart = [14, 0, -8, 5, -5, 0, -8, -5];
      g.poly(xf(dart, -PI / 4 - 0.1, 1.6, 2.2, 0.82));
      g.poly(xf(dart, PI / 4 + 0.1, 1.6, -2.2, 0.82));
    },
    line(g) { g.moveTo(-9.4, 0); g.lineTo(-3.4, 0); },
  },

  /** siegecraft — a bastion. */
  bastion: {
    body(g) {
      g.poly([-12, 8, -12, 0.5, -5, -3, 0, -9.5, 5, -3, 12, 0.5, 12, 8]);
    },
    line(g) {
      g.moveTo(-8.4, 8); g.lineTo(-8.4, 2.6); g.lineTo(-3.6, 0.2);
      g.lineTo(0, -4.4); g.lineTo(3.6, 0.2); g.lineTo(8.4, 2.6); g.lineTo(8.4, 8);
      g.moveTo(-12, 10.6); g.lineTo(12, 10.6);
    },
  },

  /** territory — a hex, wound so the nonzero rule leaves the hole. */
  hex: {
    body(g) {
      g.poly(hexPoints(12.2));
      const inner = hexPoints(8.4);
      const rev: number[] = [];
      for (let i = inner.length - 2; i >= 0; i -= 2) rev.push(inner[i]!, inner[i + 1]!);
      g.poly(rev);
      g.poly(hexPoints(4));
    },
    line(g) {
      for (let k = 0; k < 3; k++) {
        const a = (60 * k * 2 - 30) * PI / 180;
        g.moveTo(Math.cos(a) * 12.8, Math.sin(a) * 12.8);
        g.lineTo(Math.cos(a) * 15, Math.sin(a) * 15);
      }
    },
  },

  /** wardroom — an officer's cap. */
  cap: {
    body(g) {
      g.poly([-9.6, -2.4, -7.6, -8.2, 7.6, -8.2, 9.6, -2.4]);
      g.poly([-10.8, -2.4, 10.8, -2.4, 10.8, 2, -10.8, 2]);
      g.poly([-12.4, 2, 12.4, 2, 9.4, 6.4, -9.4, 6.4]);
    },
    line(g) {
      g.moveTo(-9.4, 4.4); g.lineTo(9.4, 4.4);
      g.moveTo(0, -7.6); g.lineTo(0, -2.4);
    },
    over(g) {
      g.poly(starPoints(5, 3, 1.3, -PI / 2).map((v, i) => (i % 2 ? v - 0.2 : v)));
    },
  },

  /** signals — a sonar arc off a transducer. */
  sonar: {
    body(g) {
      g.poly([-12.4, 9.6, -6.2, 9.6, -6.2, 4.4, -12.4, 4.4]);
      g.circle(-8.6, 7, 1.9);
    },
    line(g) {
      for (const r of [6.4, 11, 15.6]) {
        g.moveTo(-8.6 + Math.cos(-1.48) * r, 7 + Math.sin(-1.48) * r);
        g.arc(-8.6, 7, r, -1.48, -0.12, false);
      }
    },
  },

  /** seamanship — a ship's helm. */
  helm: {
    body(g) {
      ring(g, 0, 0, 11.2, 8.6);
      for (let k = 0; k < 6; k++) {
        const a = -PI / 2 + k * PI / 3;
        bar(g, Math.cos(a) * 2.4, Math.sin(a) * 2.4, Math.cos(a) * 10.4, Math.sin(a) * 10.4, 2.4);
      }
      g.circle(0, 0, 3.2);
    },
    line(g) {
      for (let k = 0; k < 6; k++) {
        const a = -PI / 2 + k * PI / 3;
        g.moveTo(Math.cos(a) * 11.2, Math.sin(a) * 11.2);
        g.lineTo(Math.cos(a) * 14, Math.sin(a) * 14);
      }
    },
  },

  /** fellowship — two anchors, fouled together. */
  anchors: {
    body(g) { anchorBody(g, -0.34, -3.4); anchorBody(g, 0.34, 3.4); },
    line(g) { anchorLine(g, -0.34, -3.4); anchorLine(g, 0.34, 3.4); },
  },

  /** service — a ribbon bar. */
  ribbon: {
    body(g) {
      for (const y of [-9.4, -3.2, 3]) g.poly([-12, y, 12, y, 12, y + 4.8, -12, y + 4.8]);
      g.poly([-4, 7.8, 4, 7.8, 0, 12.4]);
    },
    line(g) {
      for (const y of [-9.4, -3.2, 3]) {
        g.moveTo(-3.4, y); g.lineTo(-3.4, y + 4.8);
        g.moveTo(3.4, y); g.lineTo(3.4, y + 4.8);
      }
    },
  },

  /** arcade — a marker buoy. */
  buoy: {
    body(g) {
      g.poly([-6.2, 1.6, 6.2, 1.6, 4.6, 9.6, -4.6, 9.6]);
      g.poly([0, -10.4, 4.4, -2.8, -4.4, -2.8]);
      bar(g, 0, -3.4, 0, 2, 1.8);
    },
    line(g) {
      g.moveTo(-12.4, 5.6); g.lineTo(-7, 5.6);
      g.moveTo(7, 5.6); g.lineTo(12.4, 5.6);
      g.moveTo(-8.6, -12.4); g.lineTo(-5.4, -10.2);
      g.moveTo(8.6, -12.4); g.lineTo(5.4, -10.2);
    },
  },

  /** event — a storm glass. */
  stormglass: {
    body(g) {
      g.circle(0, 3, 8.4);
      g.poly([-3, -8.4, 3, -8.4, 3, -3.4, -3, -3.4]);
      g.poly([-4.8, -12.4, 4.8, -12.4, 4.8, -8.4, -4.8, -8.4]);
    },
    line(g) {
      g.moveTo(-6.8, 2.2); g.lineTo(-2.6, -1.4); g.lineTo(1.6, 2.2); g.lineTo(5.8, -1.4);
      g.moveTo(-7.6, 5.6); g.lineTo(7.6, 5.6);
    },
  },

  /**
   * clanwar — two crossed war banners on short staffs (R1511).
   *
   * Each staff is a bar and each banner a swallow-tailed flag on the top
   * of its staff, flying outward. The two staffs cross under the flags, so
   * the pair reads as "together" and not as two darts.
   */
  pennants: {
    body(g) {
      bar(g, -8.6, 12, 7, -12, 1.8);
      bar(g, 8.6, 12, -7, -12, 1.8);
      // The right flag is wound in reverse, so both flags wind as the two
      // staffs do and the nonzero rule leaves no hole where they overlap.
      g.poly([3.6, -6.8, 12, -4.6, 10.4, -7.8, 13, -10, 7, -12]);
      g.poly([-7, -12, -13, -10, -10.4, -7.8, -12, -4.6, -3.6, -6.8]);
    },
    line(g) {
      g.moveTo(7, -12); g.lineTo(8.2, -13.8);
      g.moveTo(-7, -12); g.lineTo(-8.2, -13.8);
      g.moveTo(-12, 13.4); g.lineTo(12, 13.4);
    },
  },

  /** journey — a waypoint track. */
  compass: {
    body(g) {
      g.poly([-10.4, 4.4, -7.6, 7.2, -10.4, 10, -13.2, 7.2]);
      g.poly([-0.6, -1.2, 2.2, 1.6, -0.6, 4.4, -3.4, 1.6]);
      g.poly(xf(starPoints(5, 6.4, 2.6, -PI / 2), 0, 8.6, -6.4));
    },
    line(g) {
      dash(g, -9.2, 6, -1.8, 2.6, 2.4);
      dash(g, 0.6, 0.4, 6.4, -4.4, 2.4);
    },
  },

  /** recognition — a rosette. */
  laurel: {
    body(g) {
      g.poly([-4.8, 6.4, -1.2, 6.4, -1.8, 13.2, -3.4, 11, -5.8, 12.8]);
      g.poly([4.8, 6.4, 1.2, 6.4, 1.8, 13.2, 3.4, 11, 5.8, 12.8]);
      g.poly(starPoints(12, 11.4, 7.8, -PI / 2));
      g.circle(0, 0, 5.2);
    },
    line(g) { g.moveTo(-3.4, 0); g.arc(0, 0, 3.4, PI, -PI, true); },
    over(g) { g.poly(starPoints(5, 3.4, 1.5, -PI / 2)); },
  },

  /**
   * casino — a poker chip, four notches out of the rim.
   *
   * The rim is four bands and not one ring, because the four gaps between
   * them ARE the notches: a band lays area, so the bite reads as the plate
   * behind the badge and stays a bite at every size. The centre spot
   * stands clear of the rim, which is what tells a chip from a wheel at
   * 24 px, where the notches are two pixels wide.
   */
  chip: {
    body(g) {
      // The notch is a 30° gap centred on each quarter of the rim.
      const ro = 12.6, ri = 8.6, half = 0.26;
      for (let k = 0; k < 4; k++) {
        const a = -PI / 2 + k * PI / 2;
        band(g, 0, 0, ro, ri, a + half, a + PI / 2 - half);
      }
      g.circle(0, 0, 6.4);
    },
    line(g) {
      // One tick off the rim between two notches, so the chip carries the
      // glint every other silhouette carries outside its mass.
      for (let k = 0; k < 4; k++) {
        const a = -PI / 4 + k * PI / 2;
        g.moveTo(Math.cos(a) * 13.4, Math.sin(a) * 13.4);
        g.lineTo(Math.cos(a) * 15.4, Math.sin(a) * 15.4);
      }
    },
  },
};

/** The silhouette of one category, or the recognition rosette for a
 *  category this build has never heard of: a badge is never blank. */
export function silhouetteOf(category: string): BadgeSilhouette {
  const id = SILHOUETTE_OF.get(category) ?? category;
  return BADGE_SILHOUETTES[id] ?? BADGE_SILHOUETTES.laurel!;
}

// ---------------------------------------------------------------------------
// The ring and the set — the two functions a picked card replaces
// ---------------------------------------------------------------------------

/** The ring's radius, in design units. The test reads it to tell the
 *  gauge's arc from an arc a silhouette lays of its own. */
export const RING_R = 20;

/** How many ticks the backing ring carries: one per grade of the ladder. */
const RING_TICKS = TOP_GRADE;

/** The arc's width, in design units (`achievements.look.ring_stroke_px`). */
const RING_STROKE = ACHIEVEMENT_LOOK.ringStrokePx;

/** The least the arc may READ, in CSS px, at any size. */
const RING_MIN_STROKE = ACHIEVEMENT_LOOK.ringMinStrokePx;

/**
 * The arc's width in design units at one badge size.
 *
 * Card 02 is weakest small, because one sixth of a circle and two sixths
 * are a pixel apart at 24 px and a thin arc is the first thing that goes.
 * The width therefore never falls under `ring_min_stroke_px` of real
 * pixels, which widens the arc at 32 px and below and changes nothing at
 * 64 px. No figure here is a literal: both come from
 * `data/presentation/achievements.toml` (R962).
 */
export function ringStrokeUnits(sizePx: number): number {
  const floor = sizePx > 0 ? RING_MIN_STROKE * BADGE_BOX / sizePx : RING_STROKE;
  return Math.max(RING_STROKE, floor);
}

/**
 * The grade ring — mockup round forty-four, card 02 "depth gauge" (R962).
 *
 * A plate of the deepest ground under a wash of the grade's own ink; a
 * faint backing ring with six ticks, which is the whole ladder; and an arc
 * that fills one sixth per grade, clockwise from twelve o'clock, in the
 * grade's own ink, with a lamp at its head. The reading is progress and
 * not rank: a player sees at a glance how much water is left.
 *
 * Challenger takes a second white hairline outside the ring, because the
 * accent alone does not separate the deepest grade from the one under it
 * on a full card.
 *
 * `grade` is 1 to 6. Grade 0 is a badge the account does not hold: the
 * plate, the backing ring and the ticks stay, and no arc and no lamp are
 * drawn.
 */
function drawRing(g: MarkGraphics, grade: number, sizePx: number): void {
  const top = grade >= TOP_GRADE;
  const ink = GRADE_INK[Math.max(0, Math.min(TOP_GRADE - 1, grade - 1))]!;
  // The backing ring and its ticks take the grade-0 ink, which is the
  // Alloy pale of the shallows: the ladder a player has not walked yet.
  const faint = GRADE_INK[0]!;
  g.circle(0, 0, RING_R - 1.4).fill({ color: DEEP, alpha: 0.8 });
  g.circle(0, 0, RING_R - 1.4).fill({ color: ink, alpha: grade > 0 ? 0.1 : 0.06 });
  g.circle(0, 0, RING_R).stroke({ width: 1.1, color: faint, alpha: 0.22 });
  for (let k = 0; k < RING_TICKS; k++) {
    const a = -PI / 2 + k * TAU / RING_TICKS;
    g.moveTo(Math.cos(a) * (RING_R - 3), Math.sin(a) * (RING_R - 3));
    g.lineTo(Math.cos(a) * RING_R, Math.sin(a) * RING_R);
  }
  g.stroke({ width: 1, color: faint, alpha: 0.3, cap: 'butt' });
  if (grade <= 0) return;
  // The arc. `moveTo` first, because an arc that follows a paint joins to
  // it (the rule of `marks.ts`), and a butt cap so one sixth ends where
  // the next would start.
  const sweep = grade / TOP_GRADE * TAU;
  const a0 = -PI / 2;
  const a1 = a0 + sweep;
  const width = ringStrokeUnits(sizePx);
  g.moveTo(Math.cos(a0) * RING_R, Math.sin(a0) * RING_R);
  g.arc(0, 0, RING_R, a0, a1, false);
  g.stroke({ width, color: ink, alpha: 1, cap: 'butt' });
  if (top) {
    g.circle(0, 0, RING_R + 2.4).stroke({ width: 0.9, color: WHITE, alpha: 0.85, cap: 'round' });
  }
  // The lamp at the arc's head. It grows with the arc, so the head stays
  // a head and never a bead on a heavy line.
  const lampInk = top ? WHITE : mixColor(ink, WHITE, 0.5);
  g.circle(Math.cos(a1) * RING_R, Math.sin(a1) * RING_R, Math.max(2.2, width * 0.65))
    .fill({ color: lampInk, alpha: 1 });
}

/** The ring a recognition wears instead: gold, and no arc and no lamp,
 *  because a recognition holds no grade (achievements.md §6). */
function drawRecognitionRing(g: MarkGraphics, small: boolean): void {
  g.circle(0, 0, RING_R - 1.4).fill({ color: DEEP, alpha: 0.8 });
  g.circle(0, 0, RING_R).stroke({
    width: small ? 1.4 : 1.9, color: GOLD, alpha: 0.75, cap: 'round',
  });
}

/** The stroke and fill styles the marks set paints a silhouette with. */
const JOIN: MarkJoin = 'round';
const CAP: MarkCap = 'round';

/**
 * The silhouette — mockup round forty-four, card 07 "the marks set".
 *
 * The body is filled in one ink, the hairlines are stroked over it, and an
 * `over` shape is cut back out in the plate's own ground. It is the
 * treatment every hull, hold and site mark of the map already wears, so a
 * badge and the thing it counts read as one family.
 */
function paintSilhouette(g: MarkGraphics, s: BadgeSilhouette, ink: number, small: boolean): void {
  s.body(g);
  g.fill({ color: ink, alpha: 1 });
  if (!small && s.line) {
    s.line(g);
    g.stroke({ width: 1.1, color: ink, alpha: 0.85, join: JOIN, cap: CAP });
  }
  if (s.over) {
    s.over(g);
    g.fill({ color: DEEP, alpha: 1 });
  }
}

// ---------------------------------------------------------------------------
// The master
// ---------------------------------------------------------------------------

/**
 * One badge, centred on the origin of `g` and `sizePx` across.
 *
 * `grade` is 1 to 6 for a grade the account holds and 0 for one it does
 * not. `category` is an id of `ACHIEVEMENT_CATEGORIES`; the `recognition`
 * category takes the gold ring and ignores the grade.
 */
export function drawBadge(
  g: MarkGraphics, category: string, grade: number, sizePx: number,
): void {
  const small = sizePx < SMALL_PX;
  const held = Math.max(0, Math.min(TOP_GRADE, Math.floor(grade)));
  const s = new ScaledMarks(g, sizePx / BADGE_BOX);
  const recognition = category === 'recognition';
  if (recognition) drawRecognitionRing(s, small);
  else drawRing(s, held, sizePx);

  // The silhouette stands in a box of its own inside the ring, which is
  // what `0.78` is: the ±13 box at 78 % clears the ring and its ticks.
  const inner = new ScaledMarks(g, (sizePx / BADGE_BOX) * 0.78);
  const ink = recognition ? GOLD : gradeSilhouetteInk(held);
  paintSilhouette(inner, silhouetteOf(category), ink, small);
}

/** The silhouette ink of a graded badge: dim at no grade, near white at the top. */
function gradeSilhouetteInk(held: number): number {
  if (held <= 0) return mixColor(GRADE_INK[0]!, DEEP, 0.45);
  if (held >= TOP_GRADE) return mixColor(GRADE_INK[TOP_GRADE - 1]!, WHITE, 0.82);
  return mixColor(GRADE_INK[held - 1]!, WHITE, 0.34);
}

// ---------------------------------------------------------------------------
// The DOM bake
// ---------------------------------------------------------------------------

/** The baked bitmaps, keyed by category, grade, size and device ratio. */
const baked = new Map<string, HTMLCanvasElement>();

/** How many bitmaps the cache keeps. Twenty-one categories at seven grades
 *  and two sizes is the whole hub; the cap is there so a long session in
 *  the category pages cannot grow without an end. */
const BAKE_CAP = 320;

/** The device ratio, clamped: a 3× phone would bake a 192 px bitmap for a
 *  64 px badge and read no better than 2× does. */
function ratio(): number {
  return Math.max(1, deviceRatio(MAX_DPR));
}

/**
 * One badge as a canvas, baked once per (category, grade, px).
 *
 * The DOM asks for this; nothing here runs per frame (§10.2). A caller
 * that mounts the same badge twice gets two elements and one bake, because
 * a canvas element cannot stand in two places.
 */
export function badgeCanvas(category: string, grade: number, px: number): HTMLCanvasElement {
  const r = ratio();
  const key = `${category}|${grade}|${px}|${r}`;
  const hit = baked.get(key);
  const out = document.createElement('canvas');
  out.width = Math.round(px * r);
  out.height = Math.round(px * r);
  out.style.width = `${px}px`;
  out.style.height = `${px}px`;
  const ctx = out.getContext('2d');
  if (!ctx) return out;
  if (hit) {
    ctx.drawImage(hit, 0, 0);
    return out;
  }
  ctx.save();
  ctx.translate(out.width / 2, out.height / 2);
  drawBadge(new Canvas2DMarks(ctx), category, grade, px * r);
  ctx.restore();
  if (baked.size >= BAKE_CAP) baked.clear();
  const keep = document.createElement('canvas');
  keep.width = out.width;
  keep.height = out.height;
  keep.getContext('2d')?.drawImage(out, 0, 0);
  baked.set(key, keep);
  return out;
}
